import React, { useState, useEffect, useCallback } from "react";
import { BookOpen } from "lucide-react";
import Toast, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync } from "./utils";

const EMPTY_DATA = {
  parentExpectations: "",
  communityNeeds: "",
  admissionsTrend: "",
  futureDemand: "",
  demographicNote: "",
  strategicRecommendation: "",
  competitors: [],
  signals: [],
};

const EMPTY_COMPETITOR = { name: "", type: "", strength: "", pressure: "", note: "" };
const EMPTY_SIGNAL = { type: "", title: "", description: "", impact: "", timeline: "" };

function Tag({ color, children }) {
  const colors = {
    green: "bg-[#eaf8f1] text-[#2d9b68] border border-[#b6e8d3]",
    red: "bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0]",
    orange: "bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]",
    blue: "bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]",
    gray: "bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#e6ecf3]">
        <div>
          <div className="font-semibold text-[#233445] text-[15px]">{title}</div>
          {note && <div className="text-xs text-[#617587] mt-0.5">{note}</div>}
        </div>
        {headerRight && <div className="flex items-center gap-2 shrink-0">{headerRight}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function PreviewCard({ label, value }) {
  return (
    <div className="bg-[#f5f7fb] rounded-[14px] p-4 mb-3 last:mb-0">
      <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-[#233445]">{value || "Not added yet."}</div>
    </div>
  );
}

function InfoCard({ title, text }) {
  return (
        <div className="bg-[#eef6fc] rounded-[14px] p-4 mb-3 last:mb-0 border border-[#cfe3f3]">
      <div className="text-sm font-semibold text-[#2d81c1] mb-1">{title}</div>
      <div className="text-sm text-[#617587]">{text}</div>
    </div>
  );
}

function calculateCompletion(data) {
  let filled = 0;
  if (data.parentExpectations) filled += 1;
  if (data.communityNeeds) filled += 1;
  if (data.admissionsTrend) filled += 1;
  if (data.futureDemand) filled += 1;
  if (data.demographicNote) filled += 1;
  if (data.competitors.length > 0) filled += 1;
  if (data.competitors.length >= 2) filled += 1;
  if (data.signals.length > 0) filled += 1;
  if (data.signals.length >= 2) filled += 1;
  return Math.round((filled / 9) * 100);
}

function getPressure(data) {
  const highComp = data.competitors.filter(c => c.pressure === "High").length;
  const highThreats = data.signals.filter(s => s.type === "Threat" && s.impact === "High").length;
  if (highComp >= 2 || highThreats >= 2) return "High";
  if (data.competitors.length || data.signals.length) return "Medium";
  return "Low";
}

const SAMPLE_DATA = {
  parentExpectations: "Parents increasingly expect better English communication, stronger board results, safer school systems, visible discipline, and more future-ready learning exposure.",
  communityNeeds: "The community values trust, affordability relative to premium schools, stronger parent communication, and visible student development beyond rote academics.",
  admissionsTrend: "Stable",
  futureDemand: "Experiential learning, STEM exposure, communication skills, and future-readiness",
  demographicNote: "The school serves lower-middle to middle-income families in a growing urban belt where parents are becoming more aspirational and comparison-driven.",
  competitors: [
    { name: "Sunrise Global School", type: "Premium CBSE school", strength: "Modern campus, strong marketing, activity-rich positioning", pressure: "High", note: "Attracts aspirational parents looking for brand perception and visible exposure." },
    { name: "City Scholars Academy", type: "Affordable academic-focused school", strength: "Board results and fee-value positioning", pressure: "Medium", note: "Competes strongly on affordability and academic credibility." },
  ],
  signals: [
    { type: "Opportunity", title: "Rising demand for future-ready education", description: "Parents are beginning to value practical skills, communication, and holistic development more than before.", impact: "High", timeline: "Short-Term" },
    { type: "Threat", title: "More aggressive competitor branding", description: "Nearby schools are investing more in branding, admissions marketing, and visible positioning.", impact: "High", timeline: "Immediate" },
    { type: "Opportunity", title: "Growing parent interest in structured progress tracking", description: "Families are showing higher interest in schools that can clearly demonstrate student growth and outcomes.", impact: "Medium", timeline: "Short-Term" },
  ],
};

export default function MarketDynamics() {
  const [activeTab, setActiveTab] = useState("context");
  const [data, setData] = useState(EMPTY_DATA);
  const [competitor, setCompetitor] = useState(EMPTY_COMPETITOR);
  const [editingCompIdx, setEditingCompIdx] = useState(null);
  const [signal, setSignal] = useState(EMPTY_SIGNAL);
  const [editingSigIdx, setEditingSigIdx] = useState(null);
  const [search, setSearch] = useState("");
  const [competitorTypeFilter, setCompetitorTypeFilter] = useState("");
  const [signalTypeFilter, setSignalTypeFilter] = useState("");
  const school = getSchoolSettings();

  useEffect(() => {
    const stored = readStorage(STORAGE_KEYS.market, null);
    if (stored) {
      setData({ ...EMPTY_DATA, ...stored, competitors: Array.isArray(stored.competitors) ? stored.competitors : [], signals: Array.isArray(stored.signals) ? stored.signals : [] });
    }
    loadModuleData("market", null).then(d => {
      if (d) setData({ ...EMPTY_DATA, ...d, competitors: Array.isArray(d.competitors) ? d.competitors : [], signals: Array.isArray(d.signals) ? d.signals : [] });
    });
  }, []);

  const saveModule = useCallback((d, action = "Market dynamics saved") => {
    saveModuleData("market", d, 0);
    addActivityAsync("Market Dynamics", action, "Saved", "green");
  }, []);

  const completion = calculateCompletion(data);
  const pressure = getPressure(data);

  const contextStatus =
    completion >= 85 ? "The school now has a strong external picture of expectations, competition, and market signals."
    : completion >= 55 ? "The school has a usable market view, but some external factors still need clarity."
    : completion > 0 ? "Some external context is saved, but the picture is still incomplete."
    : "No market context saved yet.";

  const marketInterpretation =
    completion >= 85 ? "The school has a reasonably strong understanding of external pressures, expectations, and opportunity signals."
    : completion >= 55 ? "The school has partial external awareness, but more context is still needed for sharper diagnosis."
    : "The school still has weak visibility into external expectations and pressures.";

  function handleSaveContext(isDraft = false) {
    if (!data.parentExpectations || !data.communityNeeds || !data.admissionsTrend) {
      showToast("Parent Expectations, Community Needs, and Admissions Trend are required.", "error");
      return;
    }
    const label = isDraft ? "Market context draft saved" : "Market context saved";
    saveModule(data, label);
    showToast(isDraft ? "Market context draft saved." : "Market context saved.", "success");
  }

  function handleAddCompetitor() {
    const { name, type, strength, pressure: p } = competitor;
    if (!name || !type || !strength || !p) {
      showToast("Competitor Name, Type, Main Strength, and Pressure Level are required.", "error");
      return;
    }
    let updated;
    if (editingCompIdx !== null) {
      updated = data.competitors.map((c, i) => i === editingCompIdx ? competitor : c);
      showToast("Competitor updated.", "success");
    } else {
      updated = [...data.competitors, competitor];
      showToast("Competitor added.", "success");
    }
    const newData = { ...data, competitors: updated };
    setData(newData);
    saveModule(newData, editingCompIdx !== null ? "Competitor updated" : "Competitor added");
    setCompetitor(EMPTY_COMPETITOR);
    setEditingCompIdx(null);
  }

  function editCompetitor(idx) {
    setCompetitor({ ...data.competitors[idx] });
    setEditingCompIdx(idx);
    showToast("Competitor loaded for editing.", "success");
  }

  function deleteCompetitor(idx) {
    if (!window.confirm("Delete this competitor?")) return;
    const updated = data.competitors.filter((_, i) => i !== idx);
    const newData = { ...data, competitors: updated };
    setData(newData);
    saveModule(newData, "Competitor deleted");
    showToast("Competitor deleted.", "warn");
    if (editingCompIdx === idx) { setCompetitor(EMPTY_COMPETITOR); setEditingCompIdx(null); }
  }

  function handleAddSignal() {
    const { type, title, description, impact, timeline } = signal;
    if (!type || !title || !description || !impact || !timeline) {
      showToast("Signal Type, Title, Description, Impact, and Timeline are required.", "error");
      return;
    }
    let updated;
    if (editingSigIdx !== null) {
      updated = data.signals.map((s, i) => i === editingSigIdx ? signal : s);
      showToast("External signal updated.", "success");
    } else {
      updated = [...data.signals, signal];
      showToast("External signal added.", "success");
    }
    const newData = { ...data, signals: updated };
    setData(newData);
    saveModule(newData, editingSigIdx !== null ? "External signal updated" : "External signal added");
    setSignal(EMPTY_SIGNAL);
    setEditingSigIdx(null);
  }

  function editSignal(idx) {
    setSignal({ ...data.signals[idx] });
    setEditingSigIdx(idx);
    showToast("Signal loaded for editing.", "success");
  }

  function deleteSignal(idx) {
    if (!window.confirm("Delete this signal?")) return;
    const updated = data.signals.filter((_, i) => i !== idx);
    const newData = { ...data, signals: updated };
    setData(newData);
    saveModule(newData, "External signal deleted");
    showToast("External signal deleted.", "warn");
    if (editingSigIdx === idx) { setSignal(EMPTY_SIGNAL); setEditingSigIdx(null); }
  }

  function handleFillSample() {
    setData(SAMPLE_DATA);
    saveModule(SAMPLE_DATA, "Sample market dynamics data loaded");
    showToast("Sample Market Dynamics content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved data in Market Dynamics. Continue?")) return;
    setData(EMPTY_DATA);
    setCompetitor(EMPTY_COMPETITOR);
    setEditingCompIdx(null);
    setSignal(EMPTY_SIGNAL);
    setEditingSigIdx(null);
    saveModuleData("market", EMPTY_DATA, 0);
    addActivityAsync("Market Dynamics", "Module cleared", "Cleared", "red");
    showToast("Market Dynamics module cleared.", "warn");
  }

  const pressureColor = pressure === "High" ? "red" : pressure === "Medium" ? "orange" : "blue";

  const competitorTypeOptions = [...new Set(data.competitors.map(c => c.type).filter(Boolean))];
  const SIGNAL_TYPE_OPTIONS = ["Opportunity", "Threat"];

  const filteredCompetitors = data.competitors
    .filter(c => !search || `${c.name} ${c.type} ${c.strength} ${c.pressure}`.toLowerCase().includes(search.toLowerCase()))
    .filter(c => !competitorTypeFilter || c.type === competitorTypeFilter);

  const filteredSignals = data.signals
    .filter(s => !search || `${s.type} ${s.title} ${s.impact} ${s.timeline}`.toLowerCase().includes(search.toLowerCase()))
    .filter(s => !signalTypeFilter || s.type === signalTypeFilter);

  const tabs = [
    { id: "context", label: "Parent & Community Context" },
    { id: "competition", label: "Competition" },
    { id: "signals", label: "Opportunities & Threats" },
    { id: "preview", label: "Live Preview" },
  ];

  const extraControls = (
    <div className="flex items-center gap-2">
      <select
        className="text-sm border border-[#e6ecf3] rounded-[10px] px-3 py-1.5 bg-white text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20"
        value={data.admissionsTrend || ""}
        onChange={e => {
          const newData = { ...data, admissionsTrend: e.target.value };
          setData(newData);
          saveModule(newData, "Admissions trend updated");
          showToast("Admissions trend updated.", "success");
        }}
      >
        <option value="">Admissions Trend</option>
        <option>Growing</option>
        <option>Stable</option>
        <option>Declining</option>
      </select>
      <button onClick={handleFillSample} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1] hover:bg-[#ffe8d0] transition-colors" title="Fill sample data">✨</button>
      <button onClick={handleClear} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors" title="Clear module">⌫</button>
    </div>
  );

  return (
    <>
      <div className="flex-1 flex flex-col min-w-0 border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
          {/* Hero Header */}
          <section className="mb-8">
            <div className="text-center px-4 py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Market Dynamics</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                Capture external expectations, competition pressure, and opportunity signals to sharpen school strategy.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { label: "Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "market data filled" },
              { label: "Competitors", icon: "🏫", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: data.competitors.length, meta: "schools tracked" },
              { label: "Signals", icon: "📡", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: data.signals.length, meta: "external signals logged" },
              { label: "External Pressure", icon: "⚑", iconCls: pressureColor === "red" ? "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]" : pressureColor === "orange" ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: pressure, meta: "competitive pressure" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-[10px] mb-4">
                  <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                  <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                    {s.icon}
                  </div>
                </div>
                <div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Progress Bar */}
          <div className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#233445]">Module Completion</span>
              <span className="text-sm font-bold text-[#f47c20]">{completion}%</span>
            </div>
            <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
              <div className="h-full bg-[#f47c20] rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-white rounded-[18px] border border-[#e6ecf3] p-1.5 shadow-sm w-fit">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-[12px] text-sm font-medium transition-colors ${activeTab === t.id ? "bg-[#f47c20] text-white shadow-sm" : "text-[#617587] hover:text-[#233445] hover:bg-[#f5f7fb]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* CONTEXT TAB */}
          {activeTab === "context" && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">
              <Window
                title="Parent Expectations & Local Context"
                note="Capture what the outside world expects from the school."
                headerRight={
                  <>
                    <button onClick={() => handleSaveContext(true)} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Save Draft</button>
                    <button onClick={() => handleSaveContext(false)} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">Save Context</button>
                  </>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Parent Expectations</label>
                    <textarea
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      rows={3}
                      placeholder="What do parents currently expect most from the school? Example: stronger English fluency, discipline, better board results, future-ready learning, more communication..."
                      value={data.parentExpectations}
                      onChange={e => setData(d => ({ ...d, parentExpectations: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Community Needs</label>
                    <textarea
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      rows={3}
                      placeholder="What does the surrounding community need from the school? Example: affordability, trust, safety, transportation, skill-focused education..."
                      value={data.communityNeeds}
                      onChange={e => setData(d => ({ ...d, communityNeeds: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Admissions Trend</label>
                    <select
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      value={data.admissionsTrend}
                      onChange={e => setData(d => ({ ...d, admissionsTrend: e.target.value }))}
                    >
                      <option value="">Select trend</option>
                      <option>Growing</option>
                      <option>Stable</option>
                      <option>Declining</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Future Demand Area</label>
                    <input
                      type="text"
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      placeholder="Example: experiential learning, STEM, communication skills"
                      value={data.futureDemand}
                      onChange={e => setData(d => ({ ...d, futureDemand: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Demographic / Locality Note</label>
                    <textarea
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      rows={3}
                      placeholder="Describe the locality and the broad profile of families the school serves..."
                      value={data.demographicNote}
                      onChange={e => setData(d => ({ ...d, demographicNote: e.target.value }))}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Strategic Recommendation</label>
                    <textarea
                      className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white"
                      rows={3}
                      placeholder="What strategic direction should the school take given the above market context? Can be filled manually or via AI Quick Start on Institutional Identity..."
                      value={data.strategicRecommendation}
                      onChange={e => setData(d => ({ ...d, strategicRecommendation: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-[#f5f7fb] rounded-[10px] text-sm text-[#617587]">{contextStatus}</div>
              </Window>

              <Window title="Why this matters" note="How this page improves planning quality.">
                <InfoCard title="Tells the school what the market is asking for" text="Without understanding parent and community expectations, planning becomes too inward and disconnected." />
                <InfoCard title="Adds urgency and realism" text="Admissions trend, outside demand, and community needs tell the school how fast it needs to respond and where." />
                <InfoCard title="Feeds diagnosis and SWOT" text="Much of the later SWOT and diagnosis will depend on what the external world expects versus what the school currently offers." />
                <InfoCard title="Supports better positioning" text="This page helps the school think not just like an institution, but like a school competing for trust and relevance." />
              </Window>
            </div>
          )}

          {/* COMPETITION TAB */}
          {activeTab === "competition" && (
            <Window
              title="Nearby Competition Mapping"
              note="Track competing schools, their strengths, and what pressure they create."
              headerRight={
                <>
                  <button onClick={() => { setCompetitor(EMPTY_COMPETITOR); setEditingCompIdx(null); }} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset Input</button>
                  <button onClick={handleAddCompetitor} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">
                    {editingCompIdx !== null ? "Update Competitor" : "Add Competitor"}
                  </button>
                </>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Competitor School Name</label>
                  <input type="text" className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" placeholder="Example: XYZ International School" value={competitor.name} onChange={e => setCompetitor(c => ({ ...c, name: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Type / Positioning</label>
                  <input type="text" className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" placeholder="Example: Premium CBSE school" value={competitor.type} onChange={e => setCompetitor(c => ({ ...c, type: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Main Strength</label>
                  <input type="text" className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" placeholder="Example: modern campus and marketing" value={competitor.strength} onChange={e => setCompetitor(c => ({ ...c, strength: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Pressure Level</label>
                  <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={competitor.pressure} onChange={e => setCompetitor(c => ({ ...c, pressure: e.target.value }))}>
                    <option value="">Select pressure</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Competitive Note</label>
                  <textarea className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" rows={2} placeholder="Why does this competitor matter to the school?" value={competitor.note} onChange={e => setCompetitor(c => ({ ...c, note: e.target.value }))} />
                </div>
              </div>

              {data.competitors.length > 0 && (
                <div className="mb-4 max-w-xs">
                  <label className="block text-sm font-medium text-[#233445] mb-1.5">Filter by Type</label>
                  <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={competitorTypeFilter} onChange={e => setCompetitorTypeFilter(e.target.value)}>
                    <option value="">All Types</option>
                    {competitorTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                      {["Competitor", "Type", "Main Strength", "Pressure", "Actions"].map(h => (
                        <th key={h} className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompetitors.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-8 text-[#8ea0b1] text-sm">{data.competitors.length ? "No competitors match the current filter." : "No competitors added yet."}</td></tr>
                    ) : filteredCompetitors.map((item, idx) => {
                      const realIdx = data.competitors.indexOf(item);
                      const pColor = item.pressure === "High" ? "red" : item.pressure === "Medium" ? "orange" : "blue";
                      return (
                        <tr key={idx} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe] transition-colors">
                          <td className="px-4 py-3 font-medium text-[#233445]">{item.name}</td>
                          <td className="px-4 py-3 text-[#617587]">{item.type}</td>
                          <td className="px-4 py-3 text-[#617587]">{item.strength}</td>
                          <td className="px-4 py-3"><Tag color={pColor}>{item.pressure}</Tag></td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button onClick={() => editCompetitor(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3] hover:bg-[#d4edfa] transition-colors">Edit</button>
                              <button onClick={() => deleteCompetitor(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors">Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Window>
          )}

          {/* SIGNALS TAB */}
          {activeTab === "signals" && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">
              <Window
                title="External Opportunities & Threats"
                note="Capture external signals that may affect the school."
                headerRight={
                  <>
                    <button onClick={() => { setSignal(EMPTY_SIGNAL); setEditingSigIdx(null); }} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset Input</button>
                    <button onClick={handleAddSignal} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">
                      {editingSigIdx !== null ? "Update Signal" : "Add Signal"}
                    </button>
                  </>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Signal Type</label>
                    <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={signal.type} onChange={e => setSignal(s => ({ ...s, type: e.target.value }))}>
                      <option value="">Select type</option>
                      <option>Opportunity</option>
                      <option>Threat</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Signal Title</label>
                    <input type="text" className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" placeholder="Example: Rising demand for STEM-focused schools" value={signal.title} onChange={e => setSignal(s => ({ ...s, title: e.target.value }))} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Signal Description</label>
                    <textarea className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" rows={2} placeholder="Describe the signal and why it matters..." value={signal.description} onChange={e => setSignal(s => ({ ...s, description: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Impact Level</label>
                    <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={signal.impact} onChange={e => setSignal(s => ({ ...s, impact: e.target.value }))}>
                      <option value="">Select impact</option>
                      <option>High</option>
                      <option>Medium</option>
                      <option>Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Timeline Nature</label>
                    <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={signal.timeline} onChange={e => setSignal(s => ({ ...s, timeline: e.target.value }))}>
                      <option value="">Select timeline</option>
                      <option>Immediate</option>
                      <option>Short-Term</option>
                      <option>Long-Term</option>
                    </select>
                  </div>
                </div>

                {data.signals.length > 0 && (
                  <div className="mb-4 max-w-xs">
                    <label className="block text-sm font-medium text-[#233445] mb-1.5">Filter by Type</label>
                    <select className="w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white" value={signalTypeFilter} onChange={e => setSignalTypeFilter(e.target.value)}>
                      <option value="">All Types</option>
                      {SIGNAL_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                        {["Type", "Title", "Impact", "Timeline", "Actions"].map(h => (
                          <th key={h} className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSignals.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-[#8ea0b1] text-sm">{data.signals.length ? "No external signals match the current filter." : "No external signals added yet."}</td></tr>
                      ) : filteredSignals.map((item, idx) => {
                        const realIdx = data.signals.indexOf(item);
                        return (
                          <tr key={idx} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe] transition-colors">
                            <td className="px-4 py-3"><Tag color={item.type === "Opportunity" ? "green" : "red"}>{item.type}</Tag></td>
                            <td className="px-4 py-3 font-medium text-[#233445]">{item.title}</td>
                            <td className="px-4 py-3"><Tag color={item.impact === "High" ? "red" : item.impact === "Medium" ? "orange" : "blue"}>{item.impact}</Tag></td>
                            <td className="px-4 py-3 text-[#617587]">{item.timeline}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button onClick={() => editSignal(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3] hover:bg-[#d4edfa] transition-colors">Edit</button>
                                <button onClick={() => deleteSignal(realIdx)} className="px-3 py-1 text-xs rounded-[8px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors">Delete</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Window>

              <Window title="Saved Market Snapshot" note="Quick interpretation of the saved external picture.">
                <PreviewCard label="Admissions Trend" value={data.admissionsTrend} />
                <PreviewCard
                  label="Parent & Community Context"
                  value={[
                    data.parentExpectations ? `Parents: ${data.parentExpectations}` : "",
                    data.communityNeeds ? `Community: ${data.communityNeeds}` : "",
                  ].filter(Boolean).join(" | ") || null}
                />
                <PreviewCard
                  label="External Signal Summary"
                  value={data.signals.length
                    ? `${data.signals.length} external signal(s) captured, including ${data.signals.slice(0, 2).map(s => s.title).join(" and ")}.`
                    : null}
                />
                <PreviewCard
                  label="Competitive Situation"
                  value={data.competitors.length ? `${data.competitors.length} competitor(s) tracked.` : null}
                />
              </Window>
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === "preview" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window title="Live Market Summary" note="Current external environment snapshot.">
                <PreviewCard
                  label="Market Context"
                  value={[
                    data.admissionsTrend ? `Admissions: ${data.admissionsTrend}` : "",
                    data.futureDemand ? `Future Demand: ${data.futureDemand}` : "",
                    data.demographicNote ? `Local Context: ${data.demographicNote}` : "",
                  ].filter(Boolean).join(" | ") || null}
                />
                <PreviewCard
                  label="Competition Snapshot"
                  value={data.competitors.length
                    ? `${data.competitors.length} competitor(s) tracked, with ${data.competitors.filter(c => c.pressure === "High").length} high-pressure competitor(s).`
                    : "No competitors saved yet."}
                />
                <PreviewCard
                  label="Opportunity & Threat Snapshot"
                  value={data.signals.length
                    ? `${data.signals.filter(s => s.type === "Opportunity").length} opportunity signal(s) and ${data.signals.filter(s => s.type === "Threat").length} threat signal(s) captured.`
                    : "No external signals saved yet."}
                />
                <PreviewCard
                  label="Practical Meaning"
                  value={
                    completion >= 85 ? "The school now has enough external awareness to support sharper diagnosis, SWOT, and goal setting."
                    : completion >= 55 ? "The school has a partial external picture, but some important market signals may still be missing."
                    : "The school still lacks enough external context to judge pressure or opportunity."
                  }
                />
              </Window>

              <Window title="Readiness Interpretation" note="What this module tells us for the next stage.">
                <InfoCard title="External awareness level" text={marketInterpretation} />
                <InfoCard title="Why this matters next" text="Diagnosis becomes sharper when the school can compare internal reality against outside expectations and competition." />
                <InfoCard title="Next connected page" text="After this, move to Diagnosis so the school's current internal reality can be assessed against this external context." />
              </Window>
            </div>
          )}
          </main>
        </div>
      </div>
      <Toast />
    </>
  );
}
